import * as React from 'react'
import { Factory, ListChecks, MapPin } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetAuthzMeQuery,
  useGetProblemTaskQuery,
  useListCustomAgentTasksQuery,
  useListProblemTaskGroupsQuery,
  useWorkScope,
  workScopeKey
} from '@axilon-apps/domains-ontology-ui-state'
import { ComponentChip } from '../../components/primitives/ComponentChip'
import { Empty } from '../../components/primitives/Empty'
import { OccurrenceTasks, WorkError } from '../../components/problem-work/OccurrenceTasks'
import {
  groupOccurrences,
  problemTaskStatus,
  visibleTaskGroups
} from '../../components/problem-work/work-model'
import {
  occurrenceHref,
  PROBLEM_TASK_QUERY_PARAM,
  TASK_QUERY_PARAM
} from '../../lib/component-links'
import { formatCompactLocalTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { useChatAppContext } from '../../chat-app-context'
import {
  countTasksByStatus,
  groupTasksByRun,
  parseTaskStatus,
  taskItemId,
  taskStatus,
  taskTitle,
  TASK_STATUSES
} from './tasks-helpers'
import { ReportTasks } from './ReportTasks'

export default function TasksPage() {
  const scope = useWorkScope()
  return <ScopedTasksPage key={workScopeKey(scope)} />
}
function ScopedTasksPage() {
  const scope = useWorkScope()
  const [params, setParams] = useSearchParams()
  const tab = parseTaskStatus(params.get('status'))
  const canonicalId = params.get(PROBLEM_TASK_QUERY_PARAM)
  const legacyId = params.get(TASK_QUERY_PARAM)
  const selectedId = canonicalId ?? legacyId
  const [retainedTabs, setRetained] = React.useState<Record<string, ReadonlySet<string>>>(
    () => ({})
  )
  const [retainedRuns, setRetainedRuns] = React.useState<ReadonlySet<string>>(() => new Set())
  const query = useListProblemTaskGroupsQuery(scope, { refetchOnMountOrArgChange: true })
  const { data: authz } = useGetAuthzMeQuery()
  const canReadLegacy = authz?.permissions.includes('agents:custom:config/read') === true
  const legacy = useListCustomAgentTasksQuery(scope, {
    skip: !canReadLegacy,
    refetchOnMountOrArgChange: true
  })
  const groups = query.isError ? [] : (query.currentData ?? [])
  const legacyTasks = legacy.isError || !canReadLegacy ? [] : (legacy.currentData ?? [])
  const selectedQuery = useGetProblemTaskQuery(
    { ...scope, id: canonicalId ?? '' },
    { skip: !canonicalId }
  )
  const selectedTask = selectedQuery.isError ? undefined : selectedQuery.currentData
  const selectedLegacy = legacyTasks.find(
    (task) => task.id === legacyId || taskItemId(task) === legacyId
  )
  const retained = retainedTabs[tab] ?? new Set<string>()
  const selectedGroup = selectedTask
    ? groups.find((group) => group.occurrence.id === selectedTask.occurrence_id)
    : undefined
  const selectedStatus =
    selectedTask && selectedGroup && !selectedTask.retired_at
      ? problemTaskStatus(selectedTask)
      : selectedLegacy
        ? taskStatus(selectedLegacy)
        : undefined
  const isCanonicalContext =
    selectedTask &&
    (retained.has(selectedTask.occurrence_id) ||
      (params.get('status') === 'open' &&
        !!selectedGroup?.counts.open &&
        selectedStatus === 'done'))
  const isLegacyContext =
    selectedLegacy &&
    tab === 'open' &&
    selectedStatus === 'done' &&
    (retainedRuns.has(selectedLegacy.run_id) ||
      (params.get('status') === 'open' &&
        legacyTasks.some(
          (task) => task.run_id === selectedLegacy.run_id && taskStatus(task) === 'open'
        )))
  const resolvedSelection = React.useRef<string | null>(null)
  const status =
    selectedId === resolvedSelection.current || isCanonicalContext || isLegacyContext
      ? tab
      : (selectedStatus ?? tab)
  useChatAppContext(
    selectedTask && selectedGroup
      ? [
          { key: 'Work source', value: 'occurrence' },
          { key: 'Problem occurrence id', value: selectedTask.occurrence_id },
          { key: 'Problem concept id', value: selectedGroup.occurrence.problem_id },
          { key: 'Task id', value: selectedTask.id },
          { key: 'Task', value: selectedTask.title },
          ...(selectedTask.producing_run_id
            ? [{ key: 'Producing run id', value: selectedTask.producing_run_id }]
            : [])
        ]
      : selectedLegacy
        ? [
            { key: 'Work source', value: 'legacy_result' },
            { key: 'Result item id', value: taskItemId(selectedLegacy) },
            { key: 'Run id', value: selectedLegacy.run_id },
            { key: 'Task', value: taskTitle(selectedLegacy) }
          ]
        : undefined
  )
  React.useEffect(() => {
    // Link resolution must not turn a successful completion into a tab change.
    if (!selectedId) resolvedSelection.current = null
    else if (selectedStatus) resolvedSelection.current = selectedId
    if (status === tab) return
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        next.set('status', status)
        return next
      },
      { replace: true }
    )
  }, [selectedId, selectedStatus, status, tab, setParams])
  const counts = countTasksByStatus(legacyTasks)
  for (const group of groups) for (const key of TASK_STATUSES) counts[key] += group.counts[key]
  const locations = groupOccurrences(
    visibleTaskGroups(groups, status, retained),
    (group) => group.occurrence
  )
  const reportGroups = groupTasksByRun(legacyTasks, {
    status,
    retainedRunIds: retainedRuns
  })
  const select = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id) next.set(PROBLEM_TASK_QUERY_PARAM, id)
        else next.delete(PROBLEM_TASK_QUERY_PARAM)
        next.delete(TASK_QUERY_PARAM)
        next.set('status', status)
        return next
      },
      { replace: true }
    )
  const changeTab = (value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('status', value)
        next.delete(TASK_QUERY_PARAM)
        next.delete(PROBLEM_TASK_QUERY_PARAM)
        return next
      },
      { replace: true }
    )
  const retain = (id: string) => {
    setRetained((previous) => ({ ...previous, [status]: new Set(previous[status] ?? []).add(id) }))
  }
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <header className="flex flex-col gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight text-ink">Tasks</h1>
        <div
          role="group"
          aria-label="Task status"
          className="flex items-center gap-6 border-b border-line"
        >
          {TASK_STATUSES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={status === value}
              onClick={() => changeTab(value)}
              className={cn(
                'relative -mb-px inline-flex items-center gap-2 border-b-2 px-0.5 pb-2.5 pt-1 text-13 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand motion-reduce:transition-none',
                status === value
                  ? 'border-brand text-ink'
                  : 'border-transparent text-ink-3 hover:text-ink'
              )}
            >
              {value === 'open' ? 'Open' : value === 'done' ? 'Done' : 'Rejected'}
              {!query.isError && !legacy.isError && query.currentData && (
                <span className="rounded bg-bg-sunk px-1.5 py-0.5 text-11 leading-none tabular-nums text-ink-3">
                  {counts[value]}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>
      {query.isError ? (
        <WorkError message="Couldn't load tasks." retry={() => void query.refetch()} />
      ) : !query.currentData ? (
        <p role="status" className="m-0 py-8 text-13 text-ink-3">
          Loading tasks…
        </p>
      ) : (
        <div className="flex min-w-0 flex-col gap-6 pt-1">
          {canonicalId && selectedQuery.isError && (
            <WorkError message="This task is no longer available." />
          )}
          {canonicalId && !selectedQuery.isError && selectedTask && !selectedGroup && (
            <p role="status" className="text-13 text-ink-3">
              This task is not available in this branch.
            </p>
          )}
          {locations.map(({ location, items }) => (
            <section key={location ?? '__unset__'} className="flex min-w-0 flex-col gap-2">
              <header className="flex items-center gap-2 px-1">
                {location ? (
                  <Factory size={14} className="text-ink-3" />
                ) : (
                  <MapPin size={14} className="text-ink-3" />
                )}
                <h2 className="m-0 text-13 font-medium text-ink-2">
                  {location ?? 'Location not set'}
                </h2>
                <span className="text-12 tabular-nums text-ink-3">
                  {items.reduce((sum, group) => sum + group.counts[status], 0)}
                </span>
              </header>
              <div className="flex min-w-0 flex-col gap-3">
                {items.map((group) => (
                  <section
                    key={group.occurrence.id}
                    aria-label={group.occurrence.problem_name}
                    className="min-w-0 overflow-hidden rounded-lg border border-line/80 bg-card"
                  >
                    <header className="flex items-center justify-between gap-4 border-b border-line/70 bg-bg-sunk/35 px-3 py-2.5">
                      <div className="flex min-w-0 flex-col gap-1">
                        <h3 className="m-0 text-14 font-semibold text-ink">
                          <Link
                            to={occurrenceHref(group.occurrence.id)}
                            className="rounded break-words outline-none hover:underline focus-visible:ring-2 focus-visible:ring-brand"
                          >
                            {group.occurrence.problem_name}
                          </Link>
                        </h3>
                        <div aria-label="Equipment" className="flex min-w-0 flex-wrap gap-1.5">
                          {group.occurrence.affected_entities
                            .filter((entity) => entity.type !== 'Line')
                            .map((entity) => (
                              <ComponentChip
                                key={entity.id}
                                component={{ id: entity.id, name: entity.name, scope: 'equipment' }}
                                hideScope
                              />
                            ))}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1 text-12 tabular-nums text-ink-3">
                        <span>
                          Opened{' '}
                          <time dateTime={group.occurrence.opened_at}>
                            {formatCompactLocalTime(group.occurrence.opened_at)}
                          </time>
                        </span>
                        <span>
                          {status === 'rejected'
                            ? `${group.counts.rejected} rejected`
                            : `${group.counts.done}/${group.counts.open + group.counts.done} done`}
                        </span>
                      </div>
                    </header>
                    <OccurrenceTasks
                      scope={scope}
                      occurrence={group.occurrence}
                      status={status}
                      retained={retained.has(group.occurrence.id)}
                      details
                      selectedId={selectedId}
                      onSelect={select}
                      onChanged={retain}
                    />
                  </section>
                ))}
              </div>
            </section>
          ))}
          {canReadLegacy && legacy.isError ? (
            <WorkError message="Couldn't load legacy tasks." retry={() => void legacy.refetch()} />
          ) : (
            <ReportTasks
              groups={reportGroups}
              status={status}
              selectedId={selectedLegacy?.id ?? null}
              onSelect={(id) =>
                setParams(
                  (previous) => {
                    const next = new URLSearchParams(previous)
                    next.delete(PROBLEM_TASK_QUERY_PARAM)
                    if (id === legacyId) next.delete(TASK_QUERY_PARAM)
                    else next.set(TASK_QUERY_PARAM, id)
                    return next
                  },
                  { replace: true }
                )
              }
              onUpdate={(tasks) => {
                if (status === 'open')
                  setRetainedRuns(
                    (previous) => new Set([...previous, ...tasks.map((task) => task.run_id)])
                  )
              }}
            />
          )}
          {canReadLegacy && legacy.isLoading && (
            <p role="status" className="text-13 text-ink-3">
              Loading legacy tasks…
            </p>
          )}
          {!locations.length && !reportGroups.length && !legacy.isLoading && !legacy.isError && (
            <Empty title={`No ${status} tasks`} icon={ListChecks} />
          )}
        </div>
      )}
    </div>
  )
}
